import React, { memo } from "react";
import { Data } from "../../../store/cert/types";
import { ItemDetail } from "../../../common/item/ItemDetailViewSection";
import ItemsDetailViewSection, {
  ItemsDetail,
} from "../../../common/item/ItemsDetailViewSection";
import dayjs from "dayjs";

interface CertViewDetail {
  cert: Data;
}

const CertViewDetail = ({ cert }: CertViewDetail) => {
  const { attributes: at } = cert;

  const formatDate = (date?: string) =>
    date ? dayjs(at.DateStart).format("DD MMM YYYY hh:mm:ss a") : "N/A";

  const certType = (): string => {
    if (!at.CA) return "Client Cert";
    if (at.CASerialNumber && at.CASerialNumber.length) return "Intermediate CA";
    return "Root CA";
  };

  const certInfo =
    ([
      {
        sectionName: "Certificate Info",
        itemData: [
          { fieldName: "Type", fieldValue: certType() },
          { fieldName: "Description", fieldValue: at.Description },
          { fieldName: "Serial Number", fieldValue: at.SerialNumber },
          { fieldName: "Common Name", fieldValue: at.CommonName },
          { fieldName: "Protected", fieldValue: at.Protected },
          { fieldName: "Revocation Date", fieldValue: at.RevocationDate },
          ...(at.RevocationDate
            ? [
                {
                  fieldName: "Revocation Reason",
                  fieldValue: at.RevocationReason,
                },
              ]
            : []),
        ],
      },
      ...(certType() !== "Root CA"
        ? [
            {
              sectionName: "Issuer",
              itemData: [
                { fieldName: "Issuer CA", fieldValue: at.CADescription },
                { fieldName: "CA Description", fieldValue: at.CADescription },
                {
                  fieldName: "CA Serial Number",
                  fieldValue: at.CASerialNumber,
                },
                {
                  fieldName: "CA Certificate in PEM Format",
                  fieldValue: at.X509,
                  isCode: true,
                },
                {
                  fieldName: "CA Certificate in DER Format",
                  fieldValue: at.X509DER,
                  isCode: true,
                },
              ],
            },
          ]
        : []),
    ] as ItemDetail[]) || [];

  const certDataDetails: ItemDetail[] = [
    {
      sectionName: "Info",
      itemData: [
        { fieldName: "Country", fieldValue: at.Country },
        { fieldName: "State", fieldValue: at.State },
        { fieldName: "Locality", fieldValue: at.Locality },
        { fieldName: "ORG", fieldValue: at.Org },
        { fieldName: "ORGUnit", fieldValue: at.OrgUnit },
        { fieldName: "Title", fieldValue: at.Title },
        { fieldName: "Email", fieldValue: at.Email },
      ],
    },
    {
      sectionName: "Subject Alternative Name",
      itemData: [
        { fieldName: "SAN Critical", fieldValue: at.SANCritical },
        ...(at.RevocationDate
          ? [
              {
                fieldName: "Values",
                fieldValue: at.SAN,
              },
            ]
          : []),
      ],
    },
    {
      sectionName: "Revocation OCSP URL",
      itemData: [
        { fieldName: "OSCP Critical", fieldValue: at.OSCPCritical },
        ...(at.RevocationDate
          ? [
              {
                fieldName: "Values",
                fieldValue: at.OCSP,
              },
            ]
          : []),
      ],
    },
    {
      sectionName: "Validity",
      itemData: [
        { fieldName: "Validity in years", fieldValue: at.ValidityInYears },
        { fieldName: "Start Date", fieldValue: formatDate(at.DateStart) },
        { fieldName: "End Date", fieldValue: formatDate(at.DateEnd) },
      ],
    },
  ];

  const certData: ItemsDetail = {
    sectionName: "General Info",
    itemsData: certInfo,
  };

  const certDetails: ItemsDetail = {
    sectionName: "Certificate Details",
    itemsData: certDataDetails,
  };
  return (
    <>
      <ItemsDetailViewSection
        sectionName={certData.sectionName}
        itemsData={certData.itemsData}
      />

      <ItemsDetailViewSection
        sectionName={certDetails.sectionName}
        itemsData={certDetails.itemsData}
        lastItem
      />
    </>
  );
};

export default memo(CertViewDetail);
